import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

interface ChatMessageContentProps {
  markdown?: boolean;
  text: string;
}

const markdownComponents: Components = {
  a: ({ node: _node, children, ...props }) => (
    <a
      {...props}
      className="font-semibold text-emerald-800 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-950"
      rel="noreferrer noopener"
      target="_blank"
    >
      {children}
    </a>
  ),
  blockquote: ({ node: _node, children, ...props }) => (
    <blockquote
      {...props}
      className="my-3 border-l-4 border-emerald-300 pl-3 text-stone-600 italic first:mt-0 last:mb-0"
    >
      {children}
    </blockquote>
  ),
  code: ({ node: _node, children, ...props }) => (
    <code
      {...props}
      className="rounded bg-stone-200 px-1 py-0.5 font-mono text-[0.9em] text-stone-800"
    >
      {children}
    </code>
  ),
  h1: ({ node: _node, children, ...props }) => (
    <h1
      {...props}
      className="mb-2 mt-5 text-lg font-black leading-snug text-emerald-950 first:mt-0"
    >
      {children}
    </h1>
  ),
  h2: ({ node: _node, children, ...props }) => (
    <h2
      {...props}
      className="mb-2 mt-5 text-base font-bold leading-snug text-emerald-950 first:mt-0"
    >
      {children}
    </h2>
  ),
  h3: ({ node: _node, children, ...props }) => (
    <h3 {...props} className="mb-1.5 mt-4 font-bold leading-snug text-stone-900 first:mt-0">
      {children}
    </h3>
  ),
  hr: ({ node: _node, ...props }) => <hr {...props} className="my-4 border-stone-300" />,
  li: ({ node: _node, children, ...props }) => (
    <li {...props} className="pl-1 marker:font-semibold marker:text-emerald-800">
      {children}
    </li>
  ),
  ol: ({ node: _node, children, ...props }) => (
    <ol {...props} className="my-3 list-decimal space-y-1.5 pl-6 first:mt-0 last:mb-0">
      {children}
    </ol>
  ),
  p: ({ node: _node, children, ...props }) => (
    <p {...props} className="my-3 whitespace-pre-wrap first:mt-0 last:mb-0">
      {children}
    </p>
  ),
  pre: ({ node: _node, children, ...props }) => (
    <pre
      {...props}
      className="my-3 max-w-full overflow-x-auto rounded-xl bg-stone-900 p-3 text-sm leading-6 text-stone-100 first:mt-0 last:mb-0 [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-inherit"
    >
      {children}
    </pre>
  ),
  strong: ({ node: _node, children, ...props }) => (
    <strong {...props} className="font-bold text-stone-900">
      {children}
    </strong>
  ),
  table: ({ node: _node, children, ...props }) => (
    <div className="my-3 max-w-full overflow-x-auto rounded-xl border border-stone-200">
      <table {...props} className="w-full min-w-max border-collapse text-left text-sm">
        {children}
      </table>
    </div>
  ),
  td: ({ node: _node, children, ...props }) => (
    <td {...props} className="border-t border-stone-200 px-3 py-2 align-top">
      {children}
    </td>
  ),
  th: ({ node: _node, children, ...props }) => (
    <th {...props} className="bg-stone-100 px-3 py-2 font-bold text-stone-900">
      {children}
    </th>
  ),
  ul: ({ node: _node, children, ...props }) => (
    <ul {...props} className="my-3 list-disc space-y-1.5 pl-5 first:mt-0 last:mb-0">
      {children}
    </ul>
  ),
};

export function ChatMessageContent({ markdown = true, text }: ChatMessageContentProps) {
  if (!markdown) return <span className="whitespace-pre-wrap">{text}</span>;
  return (
    <div className="min-w-0 break-words">
      <Markdown components={markdownComponents} remarkPlugins={[remarkGfm]} skipHtml>
        {text}
      </Markdown>
    </div>
  );
}
